import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

/**
 * Defines a typesafe schema for environment variables used by the API client.
 * This enables better developer experience with autocompletion and validation.
 *
 * Usage in your project:
 * 1. Create a .env file in your project root with the desired configuration:
 *    ```
 *    FLUID_API_BASE_URL=https://api.staging.fluid.app
 *    FLUID_API_TIMEOUT=60000
 *    FLUID_API_DEBUG=true
 *    ```
 *
 * 2. Import and use the environment variables in your code:
 *    ```
 *    import { env } from "@fluid-app/api-client";
 *
 *    console.log(env.FLUID_API_BASE_URL); // https://api.staging.fluid.app
 *    ```
 *
 * Note: The environment variables are scoped to the api-client package
 * and won't conflict with other environment variables in your project.
 */

// Safely get the runtime environment
const getRuntimeEnv = () => {
  // Check if we're in a Node.js environment
  if (typeof process !== "undefined") {
    return process.env;
  }

  // Fallback for browser environments
  return {
    // Default values for client-side
    FLUID_API_BASE_URL: "https://api.fluid.app",
    FLUID_API_TIMEOUT: "30000",
    FLUID_API_DEBUG: "false",
  };
};

export const env = createEnv({
  /**
   * Server-side environment variables that won't be exposed to the client.
   * These will throw an error if accessed on the client.
   */
  server: {
    // Add server-only environment variables here if needed
  },

  /**
   * Client-side environment variables that will be accessible in both server and client contexts.
   * These should be prefixed with FLUID_API_ to avoid collisions with other environment variables.
   */
  clientPrefix: "FLUID_API_",
  client: {
    FLUID_API_BASE_URL: z.string().url().default("https://api.fluid.app"),
    FLUID_API_TIMEOUT: z.coerce.number().positive().default(30000),
    FLUID_API_DEBUG: z
      .string()
      .transform((s) => s !== "false" && s !== "0")
      .default("false"),
  },

  /**
   * Runtime environment object that contains the actual environment variables.
   * Uses process.env in Node.js or defaults in browser environments.
   */
  runtimeEnv: getRuntimeEnv(),

  /**
   * Treat empty strings as undefined for better validation experience.
   */
  emptyStringAsUndefined: true,

  /**
   * Skip validation in non-production environments or when running tests.
   */
  skipValidation:
    typeof process === "undefined" || process.env.NODE_ENV !== "production",
});
